import { PointCloudOctree } from '@pnext/three-loader';
import * as THREE from 'three';

export interface PickResult {
  position: THREE.Vector3;
  /**
   * Layer ID of the source point cloud (`pco.userData.layerId`), or `null`
   * when the closest hit was a mesh / unknown source. Lets callers (e.g.
   * the elevation-profile tool) pre-select the layer the user picked from.
   */
  layerId: string | null;
}

/**
 * Pick the closest surface point from both point cloud octrees and standard meshes.
 * Returns the closest hit or null if nothing was hit.
 */
export function pickSurface(
  renderer: THREE.WebGLRenderer,
  camera: THREE.Camera,
  raycaster: THREE.Raycaster,
  pcoTargets: PointCloudOctree[],
  meshTargets: THREE.Object3D[],
): PickResult | null {
  const results: { position: THREE.Vector3; distance: number; layerId: string | null }[] = [];

  // Point cloud picking via .pick()
  for (const pco of pcoTargets) {
    const hit = pco.pick(renderer, camera, raycaster.ray);
    if (hit?.position) {
      const pos = hit.position.clone();
      const layerId = (pco.userData as { layerId?: string } | undefined)?.layerId ?? null;
      results.push({ position: pos, distance: pos.distanceTo(raycaster.ray.origin), layerId });
    }
  }

  // Standard mesh raycasting (use a tight line threshold to avoid aggressive edge snapping)
  // Wrapped in try/catch because @thatopen/fragments LOD meshes have non-standard
  // geometry buffers that can throw when Three.js's default raycaster traverses them.
  if (meshTargets.length > 0) {
    try {
      const prevLineThreshold = raycaster.params.Line.threshold;
      raycaster.params.Line.threshold = 0.1;
      const intersections = raycaster.intersectObjects(meshTargets, true);
      raycaster.params.Line.threshold = prevLineThreshold;
      if (intersections.length > 0) {
        results.push({
          position: intersections[0].point.clone(),
          distance: intersections[0].distance,
          // Mesh hits don't have an associated point-cloud layer.
          layerId: null,
        });
      }
    } catch {
      // Fragment LOD meshes aren't compatible with standard raycasting — skip silently
    }
  }

  if (results.length === 0) return null;
  results.sort((a, b) => a.distance - b.distance);
  return { position: results[0].position, layerId: results[0].layerId };
}
